import { useEffect, useRef } from "react";

import type { ContextRef, PageState } from "../protocol/protocol";

/**
 * What the page shows, for the agent: named slots of plain JSON that page components keep current
 * (`useAgentPageState`), and the page's own context (`usePageContext` sets it). A send captures them
 * (`capturePageState`) only when the thread's context is the page's, so the assistant sees the
 * user's unsaved on-screen choices for that turn. A focused tab also sends them as it changes
 * (`onPageStateChange`), as the desktop context the assistant on WhatsApp and Telegram reads.
 * Domain pages import this; it imports none.
 */

export type PageSlotValue = Record<string, unknown>;

interface Slot {
  owner: symbol;
  value: PageSlotValue;
}

const slots = new Map<string, Slot>();
const listeners = new Set<() => void>();
let pageContext: ContextRef | null = null;

export function setPageContext(contextRef: ContextRef | null): void {
  pageContext = contextRef;
}

/** Writes the slot for `owner`; `null` removes it, but only while `owner` still holds it. */
export function setPageSlot(name: string, value: PageSlotValue | null, owner: symbol): void {
  if (value) slots.set(name, { owner, value });
  else if (slots.get(name)?.owner === owner) slots.delete(name);
  else return;
  listeners.forEach((listener) => listener());
}

/** Calls `listener` after every slot change; returns the unsubscribe. */
export function onPageStateChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The page state a message to a thread on `threadContext` carries, or null when it carries none. */
export function capturePageState(threadContext: ContextRef | null): PageState | null {
  if (!threadContext || threadContext !== pageContext || slots.size === 0) return null;
  return {
    context_ref: threadContext,
    slots: Object.fromEntries([...slots].map(([name, slot]) => [name, slot.value])),
  };
}

/**
 * Keeps slot `name` at `value` while the calling component is mounted (null: nothing to say), so
 * only state on screen is ever sent. A new value is compared by content, so building it inline is
 * fine.
 */
export function useAgentPageState(name: string, value: PageSlotValue | null): void {
  const owner = useRef(Symbol(name)).current;
  const json = value ? JSON.stringify(value) : null;
  useEffect(() => {
    setPageSlot(name, json ? (JSON.parse(json) as PageSlotValue) : null, owner);
    return () => setPageSlot(name, null, owner);
  }, [name, json, owner]);
}

/** Tests only: forget every slot and the page's context. */
export function resetPageState(): void {
  slots.clear();
  pageContext = null;
}
